import { useMemo, useState } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { cn } from '../../lib/utils';
import { formatAttributeDisplay } from '../../utils/periodizationPeriodBuckets';
import { effectiveBucketsForType } from '../../utils/periodizationPlanningScope';
import {
  buildAttributeWeekScopeMeta,
  resolveEditableTarget,
} from '../../utils/blockCalendarAttributeUtils';
import AttributeValueBadge from './AttributeValueBadge';
import BlockAttributeValuePopover from './BlockAttributeValuePopover';
import BlockAttributeSeriesModal from './BlockAttributeSeriesModal';
import BlockCalendarScopeCell from './BlockCalendarScopeCell';

const PHASE_COLORS = {
  base: 'bg-emerald-500/80',
  build: 'bg-primary/80',
  peak: 'bg-rose-500/80',
  taper: 'bg-sky-500/80',
  recovery: 'bg-muted',
  transition: 'bg-violet-500/70',
  custom: 'bg-secondary',
};

function attrLabel(t, typeKey, fallback) {
  const k = `periodization.attributes.${typeKey}`;
  const tr = t(k);
  return tr !== k ? tr : fallback || typeKey;
}

export default function BlockWeekAttributesTable({ tree, onUpdated }) {
  const { t } = useLanguage();
  const [attrSeriesModal, setAttrSeriesModal] = useState(null);

  const block = tree?.block;
  const scopeOverrides = block?.planning_scope_overrides;
  const allTypes = tree?.assessment_set?.attribute_types || [];

  const micros = useMemo(
    () =>
      (tree?.phases || []).flatMap((p) =>
        (p.microcycles || []).map((m) => ({ ...m, phase: p }))
      ),
    [tree?.phases]
  );

  const weeks = useMemo(() => micros.map((m) => m.week_start), [micros]);

  const weekTypes = useMemo(
    () =>
      allTypes.filter((type) => {
        const buckets = effectiveBucketsForType(type, scopeOverrides);
        return buckets && (buckets.week || buckets.phase || buckets.block);
      }),
    [allTypes, scopeOverrides]
  );

  const calendarBlock = useMemo(
    () => ({
      ...block,
      phases: tree?.phases || [],
    }),
    [block, tree?.phases]
  );

  if (!micros.length) {
    return (
      <p className='text-xs text-muted-foreground px-1'>{t('periodization.weekAttributes.noWeeks')}</p>
    );
  }

  if (!weekTypes.length) {
    return (
      <p className='text-xs text-muted-foreground px-1'>{t('periodization.weekAttributes.noParams')}</p>
    );
  }

  return (
    <>
      <div className='overflow-x-auto rounded-lg border border-border'>
        <table className='w-full min-w-[480px] text-xs border-collapse'>
          <thead>
            <tr className='border-b border-border bg-muted/30'>
              <th className='text-left p-2 font-medium text-muted-foreground sticky left-0 bg-card z-[1] min-w-[140px]'>
                {t('periodization.attribute')}
              </th>
              {micros.map((m) => (
                <th key={m.id} className='p-2 font-medium text-center min-w-[52px]'>
                  <div>W{m.week_number_in_block}</div>
                  <div
                    className={cn(
                      'h-1 rounded mt-1',
                      PHASE_COLORS[m.phase.phase_kind] || PHASE_COLORS.custom
                    )}
                  />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {weekTypes.map((attr) => {
              const scopeMeta = buildAttributeWeekScopeMeta(calendarBlock, weeks, attr);
              return (
              <tr key={attr.type_key} className='border-b border-border/60'>
                <td className='p-2 sticky left-0 bg-card z-[1] font-medium'>
                  <button
                    type='button'
                    className='text-left hover:text-primary hover:underline'
                    title={t('periodization.calendar.editAttrSeries')}
                    onClick={() => setAttrSeriesModal({ block: calendarBlock, attr })}
                  >
                    {attrLabel(t, attr.type_key, attr.name)}
                  </button>
                </td>
                {micros.map((m, weekIdx) => {
                  const scopeCell = scopeMeta[weekIdx] || {};
                  const resolved = {
                    value: scopeCell.value,
                    bucket: scopeCell.bucket,
                  };
                  const editTarget = resolveEditableTarget(attr, block, m.phase, m);
                  const scopeTitle = resolved.bucket
                    ? `${t(`periodization.assessmentManager.buckets.${resolved.bucket}`)} · ${formatAttributeDisplay(resolved.value, attr)}`
                    : undefined;
                  return (
                    <td key={`${m.id}-${attr.type_key}`} className='p-1.5 text-center'>
                      <BlockCalendarScopeCell
                        spanRole={scopeCell.spanRole}
                        bucket={resolved.bucket}
                        title={scopeTitle}
                      >
                        <BlockAttributeValuePopover
                          attr={attr}
                          value={resolved.value}
                          editable={editTarget.editable}
                          targetType={editTarget.targetType}
                          microId={editTarget.microId}
                          phaseId={editTarget.phaseId}
                          blockId={editTarget.blockId}
                          onSaved={onUpdated}
                        >
                          <AttributeValueBadge value={resolved.value} type={attr} compact />
                        </BlockAttributeValuePopover>
                      </BlockCalendarScopeCell>
                    </td>
                  );
                })}
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <BlockAttributeSeriesModal
        open={!!attrSeriesModal}
        onOpenChange={(next) => !next && setAttrSeriesModal(null)}
        block={attrSeriesModal?.block}
        attr={attrSeriesModal?.attr}
        weeks={weeks}
        onSaved={onUpdated}
      />
    </>
  );
}
